import { useState } from "react";
import { Alert, Badge, Button, Col, Form, ListGroup, Modal, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import useUserStore from "../stores/user-store";
import { useAnnouncementAdd, useAnnouncementDelete, useAnnouncementsQuery, useAnnouncementUpdate } from "../new/announcements.query";
import { announcementPermissions } from "../new/announcements.permissions";
import styles from "../styles/home-announcements.module.css";

const initialDraft = {
    title: "",
    body: "",
    goodUntilDate: ""
};

const validationSchema = Yup.object().shape({
    title: Yup.string().trim().max(200, "*Title cannot exceed 200 characters.").required("*Title is required."),
    body: Yup.string().trim().required("*Announcement text is required."),
    goodUntilDate: Yup.string().nullable()
});

const announcementAccentPalette = [
    {
        accent: "#446b8d",
        accentSoft: "#90b2d1",
        accentTint: "rgba(68, 107, 141, 0.12)"
    },
    {
        accent: "#4c7468",
        accentSoft: "#98bbb0",
        accentTint: "rgba(76, 116, 104, 0.12)"
    },
    {
        accent: "#627786",
        accentSoft: "#a9b9c5",
        accentTint: "rgba(98, 119, 134, 0.12)"
    }
];

const expiredAnnouncementAccent = {
    accent: "#8a919b",
    accentSoft: "#c0c6ce",
    accentTint: "rgba(138, 145, 155, 0.1)"
};

const formatTimestamp = (value) => new Date(value).toLocaleString();
const formatGoodUntilDate = (value) => value || "No expiration";

const getAnnouncementAccentStyle = (accentIndex = 0, expired = false) => {
    const accent = expired
        ? expiredAnnouncementAccent
        : announcementAccentPalette[accentIndex % announcementAccentPalette.length];

    return {
        "--announcement-accent": accent.accent,
        "--announcement-accent-soft": accent.accentSoft,
        "--announcement-accent-tint": accent.accentTint
    };
};

const HomeAnnouncements = () => {
    const { permissions, userid } = useUserStore((state) => ({
        permissions: state.permissions,
        userid: state.userid,
    }));

    const { data, isLoading, isFetching } = useAnnouncementsQuery();
    const addMutation = useAnnouncementAdd();
    const updateMutation = useAnnouncementUpdate();
    const deleteMutation = useAnnouncementDelete();

    const [errorText, setErrorText] = useState("");
    const [successText, setSuccessText] = useState("");
    const [editorMode, setEditorMode] = useState(null);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showExpired, setShowExpired] = useState(false);
    const [showEditorModal, setShowEditorModal] = useState(false);

    const userPermissions = permissions ?? [];
    const canAdd = userPermissions.includes(announcementPermissions.add);
    const isMutating = addMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
    const activeAnnouncements = data?.activeAnnouncements ?? [];
    const expiredAnnouncements = data?.expiredAnnouncements ?? [];

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors, isDirty, isSubmitting }
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: initialDraft
    });

    const titleValue = watch("title") ?? "";
    const titleCharactersRemaining = 200 - titleValue.length;

    const getCanEdit = (announcement) => {
        if (!announcement) {
            return false;
        }

        return userPermissions.includes(announcementPermissions.editAll) || (
            announcement.createdByUserId === userid &&
            userPermissions.includes(announcementPermissions.editOwn)
        );
    };

    const getCanDelete = (announcement) => {
        if (!announcement) {
            return false;
        }

        return userPermissions.includes(announcementPermissions.deleteAll) || (
            announcement.createdByUserId === userid &&
            userPermissions.includes(announcementPermissions.deleteOwn)
        );
    };

    const closeEditorModal = () => {
        setShowEditorModal(false);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
    };

    const openCreateModal = () => {
        reset(initialDraft);
        setEditingAnnouncement(null);
        setEditorMode("create");
        setShowEditorModal(true);
        setErrorText("");
        setSuccessText("");
    };

    const openEditModal = (announcement) => {
        reset({
            title: announcement.title,
            body: announcement.body,
            goodUntilDate: announcement.goodUntilDate || ""
        });
        setEditingAnnouncement(announcement);
        setEditorMode("edit");
        setShowEditorModal(true);
        setErrorText("");
        setSuccessText("");
    };

    const onSubmit = async (values) => {
        const payload = {
            title: values.title,
            body: values.body,
            goodUntilDate: values.goodUntilDate || null
        };
        try {
            if (editorMode === "create") {
                await addMutation.mutateAsync(payload);
                setSuccessText("Announcement created successfully.");
            }

            if (editorMode === "edit" && editingAnnouncement) {
                await updateMutation.mutateAsync({
                    id: editingAnnouncement.id,
                    ...payload
                });
                setSuccessText("Announcement updated successfully.");
            }

            closeEditorModal();
            setErrorText("");
        } catch (error) {
            setSuccessText("");
            setErrorText(error?.message ?? "Unable to save the announcement.");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) {
            return;
        }

        try {
            await deleteMutation.mutateAsync({ id: deleteTarget.id });
            closeDeleteModal();
            setErrorText("");
            setSuccessText("Announcement deleted successfully.");
        } catch (error) {
            setSuccessText("");
            setErrorText(error?.message ?? "Unable to delete the announcement.");
        }
    };

    const renderAnnouncementItem = (announcement, expired = false, accentIndex = 0) => {
        const canEdit = getCanEdit(announcement);
        const canDelete = getCanDelete(announcement);
        const accentStyle = getAnnouncementAccentStyle(accentIndex, expired);
        const itemLabel = expired ? "Archived" : "Announcement";

        return (
            <ListGroup.Item key={announcement.id} className={styles.item} style={accentStyle}>
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div className={styles.itemHeaderMain}>
                        <div className={styles.itemEyebrow}>
                            <span className={styles.itemSignalDot} aria-hidden="true" />
                            <span>{itemLabel}</span>
                        </div>
                        <div className={`fw-semibold ${styles.itemTitle}`}>{announcement.title}</div>
                        <small className={styles.itemMeta}>
                            {announcement.createdByUsername} - Updated {formatTimestamp(announcement.updatedAt)}
                        </small>
                    </div>
                    <div className={`d-flex align-items-center gap-2 flex-wrap ${styles.itemBadgeGroup}`}>
                        {announcement.createdByUserId === userid ? <Badge className={styles.badgeMine}>Mine</Badge> : null}
                        {expired ? <Badge className={styles.badgeExpired}>Expired</Badge> : null}
                    </div>
                </div>
                <div className={`mb-2 ${styles.itemBody}`} style={{ whiteSpace: "pre-wrap" }}>{announcement.body}</div>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <small className={styles.itemFooterMeta}>Good Until: {formatGoodUntilDate(announcement.goodUntilDate)}</small>
                    <div className="d-flex gap-2">
                        {canEdit ? (
                            <Button type="button" size="sm" variant="outline-primary" onClick={() => openEditModal(announcement)} disabled={isMutating}>
                                Edit
                            </Button>
                        ) : null}
                        {canDelete ? (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-danger"
                                onClick={() => {
                                    setDeleteTarget(announcement);
                                    setShowDeleteModal(true);
                                }}
                                disabled={isMutating}
                            >
                                Delete
                            </Button>
                        ) : null}
                    </div>
                </div>
            </ListGroup.Item>
        );
    };

    const activeEmptyState = !isLoading && activeAnnouncements.length === 0;
    const expiredCount = expiredAnnouncements.length;

    return (
        <>
            <Row className="mb-3">
                <Col xs={12}>
                    <div className={`clientCard h-100 ${styles.panel}`}>
                        <div className="clientCardInner">
                            <div className={styles.headerBand}>
                                <div>
                                    <h4 className={styles.headerTitle}>Announcements</h4>
                                    <small className={styles.headerText}>Homepage announcement feed with active and expired announcements.</small>
                                </div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {isFetching ? <small className={styles.headerText}>Refreshing...</small> : null}
                                    {expiredCount > 0 ? (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={showExpired ? "outline-secondary" : "outline-primary"}
                                            onClick={() => setShowExpired((prev) => !prev)}
                                        >
                                            {showExpired ? `Hide Expired (${expiredCount})` : `Show Expired (${expiredCount})`}
                                        </Button>
                                    ) : null}
                                    {canAdd ? (
                                        <Button type="button" size="sm" variant="success" onClick={openCreateModal} disabled={isMutating}>
                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                            Add Announcement
                                        </Button>
                                    ) : null}
                                </div>
                            </div>

                            {successText ? <Alert variant="success" className="py-2">{successText}</Alert> : null}
                            {errorText ? <Alert variant="danger" className="py-2">{errorText}</Alert> : null}

                            <div className={`dataCard mb-3 ${styles.sectionCard}`}>
                                <div className={styles.sectionHeader}>
                                    <strong>Current Announcements</strong>
                                    <Badge className={styles.badgeCount}>{activeAnnouncements.length}</Badge>
                                </div>

                                {isLoading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Loading announcements...
                                    </div>
                                ) : activeEmptyState ? (
                                    <div className={`text-center py-4 text-muted ${styles.emptyState}`}>No announcements are currently active.</div>
                                ) : (
                                    <div className={styles.scrollArea}>
                                        <ListGroup>
                                            {activeAnnouncements.map((announcement, index) => renderAnnouncementItem(announcement, false, index))}
                                        </ListGroup>
                                    </div>
                                )}
                            </div>

                            {expiredCount > 0 && showExpired ? (
                                <div className={`dataCard mb-0 ${styles.sectionCard}`}>
                                    <div className={styles.sectionHeader}>
                                        <strong>My Expired Announcements</strong>
                                        <div className="d-flex align-items-center gap-2">
                                            <Badge className={styles.badgeExpired}>{expiredCount}</Badge>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => setShowExpired(false)}
                                            >
                                                Hide
                                            </Button>
                                        </div>
                                    </div>

                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Loading expired announcements...
                                        </div>
                                    ) : (
                                        <div className={styles.scrollAreaCompact}>
                                            <ListGroup>
                                                {expiredAnnouncements.map((announcement, index) => renderAnnouncementItem(announcement, true, index))}
                                            </ListGroup>
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </Col>
            </Row>

            <Modal
                show={showEditorModal}
                onHide={closeEditorModal}
                onExited={() => {
                    setEditorMode(null);
                    setEditingAnnouncement(null);
                    reset(initialDraft);
                }}
                centered
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editorMode === "create" ? "Add Announcement" : "Edit Announcement"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="announcement-title">Title</Form.Label>
                            <Form.Control id="announcement-title" type="text" maxLength={200} {...register("title")} disabled={isSubmitting} />
                            {errors.title ? <small style={{ color: "red" }}>{errors.title.message}</small> : null}
                            <small className={titleCharactersRemaining <= 25 ? "text-warning d-block mt-1" : "text-muted d-block mt-1"}>
                                {titleCharactersRemaining} characters remaining
                            </small>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="announcement-good-until">Good Until Date</Form.Label>
                            <Form.Control id="announcement-good-until" type="date" {...register("goodUntilDate")} disabled={isSubmitting} />
                            <small className="text-muted">Optional. Leave blank if the announcement should not expire.</small>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label htmlFor="announcement-body">Announcement</Form.Label>
                            <Form.Control id="announcement-body" as="textarea" rows={8} {...register("body")} disabled={isSubmitting} />
                            {errors.body ? <small style={{ color: "red" }}>{errors.body.message}</small> : null}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type="button" variant="secondary" onClick={closeEditorModal} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={(editorMode === "edit" ? !isDirty : false) || isSubmitting}>
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : editorMode === "create" ? "Create" : "Save Changes"}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal
                show={showDeleteModal}
                onHide={closeDeleteModal}
                onExited={() => setDeleteTarget(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Announcement</Modal.Title>
                </Modal.Header>
                <Modal.Body className={styles.deleteModalBody}>
                    {deleteTarget ? (
                        <>
                            <p className="mb-2">Delete this announcement?</p>
                            <div className={`dataCard mb-0 ${styles.deletePreviewCard}`}>
                                <strong className={styles.deletePreviewTitle}>{deleteTarget.title}</strong>
                                <div
                                    className={styles.deletePreviewBody}
                                >
                                    {deleteTarget.body}
                                </div>
                                <small className={`text-muted d-block mt-2 ${styles.deletePreviewMeta}`}>Good Until: {formatGoodUntilDate(deleteTarget.goodUntilDate)}</small>
                            </div>
                        </>
                    ) : (
                        <p className="mb-0">No announcement is selected.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="secondary" onClick={closeDeleteModal} disabled={deleteMutation.isPending}>
                        Cancel
                    </Button>
                    <Button type="button" variant="danger" onClick={handleDelete} disabled={deleteMutation.isPending || !deleteTarget}>
                        {deleteMutation.isPending ? <Spinner animation="border" size="sm" /> : "Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default HomeAnnouncements;
